"use client";

import React from "react";
import { Loader, Send } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function InquiryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center p-12 text-center bg-card border border-border rounded-xl shadow-sm animate-in fade-in duration-500">
        <div className="size-12 rounded-lg bg-primary/10 flex items-center justify-center mb-6">
          <Send className="size-6 text-primary" />
        </div>
        <h3 className="font-serif text-2xl font-medium text-foreground mb-2">
          Inquiry Received
        </h3>
        <p className="text-muted-foreground mb-8 max-w-md">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="rounded-md">
          Submit Another Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="firstName" rules={{
        required: "First name is required",
        minLength: {
          value: 2,
          message: "Minimum 2 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground">First Name</FormLabel>
              <FormControl>
                <Input placeholder="Jane" className="bg-background/50 border-border focus-visible:ring-primary rounded-md" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
        <FormField control={control} name="lastName" rules={{
        required: "Last name is required",
        minLength: {
          value: 2,
          message: "Minimum 2 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground">Last Name</FormLabel>
              <FormControl>
                <Input placeholder="Doe" className="bg-background/50 border-border focus-visible:ring-primary rounded-md" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="jane@example.com" className="bg-background/50 border-border focus-visible:ring-primary rounded-md" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
        <FormField control={control} name="phone" rules={{
        required: "Phone number is required for our dedicated lines",
        pattern: {
          value: /^[0-9+\-\s()]*$/,
          message: "Please enter a valid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(555) 123-4567" className="bg-background/50 border-border focus-visible:ring-primary rounded-md" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="agreedToTexts" rules={{
      required: "You must agree to the terms to proceed"
    }} render={({
      field
    }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0 p-4 bg-background/30 border border-border/50 rounded-lg">
            <FormControl>
              <Checkbox checked={field.value} onCheckedChange={field.onChange} className="mt-1 border-primary data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground rounded-sm" />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel className="text-xs text-muted-foreground font-normal leading-relaxed cursor-pointer">
                By providing your phone number and checking this box, you agree to receive automated CRM updates, account notifications, and marketing text messages from CSOS. Msg frequency varies. Msg &amp; data rates may apply. Reply STOP to opt-out. See Terms &amp; Privacy.
              </FormLabel>
              <FormMessage />
            </div>
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Please provide details about your inquiry",
      minLength: {
        value: 10,
        message: "Please provide more details"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground">How can we help?</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about your project or inquiry..." className="min-h-[120px] bg-background/50 border-border focus-visible:ring-primary rounded-md resize-y" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
          <p className="text-sm text-destructive font-medium">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto min-w-[200px] h-12 text-base rounded-md bg-primary hover:bg-primary/90 text-primary-foreground transition-colors">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Sending Inquiry...
          </> : <>
            Submit Inquiry
            <Send className="ml-2 size-4" />
          </>}
      </Button>
    </Form>;
}
export default function ContactInquiry() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: formRef,
    isVisible: isFormVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="contact-inquiry" className="relative py-24 lg:py-32 bg-background overflow-hidden">
      <div className="container mx-auto px-6 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-12 gap-16 lg:gap-24 items-start">
          
          <div ref={headerRef} className={`lg:col-span-5 space-y-10 transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="space-y-6">
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.1] text-foreground tracking-tight">
                Start a <br className="hidden lg:block" />
                <span className="text-primary italic pr-2">conversation</span>
              </h2>
              <Separator className="w-16 h-1 bg-primary rounded-lg" />
              <p className="text-lg text-muted-foreground leading-relaxed max-w-md">
                We approach every inquiry with the attention it deserves. Share your details, and our dedicated team will connect with you promptly to discuss how we can assist.
              </p>
            </div>

            <div className="p-6 bg-card/40 border border-border/50 rounded-xl shadow-sm backdrop-blur-sm">
              <h4 className="text-sm font-semibold text-foreground mb-3 uppercase tracking-wider">
                Communication Notice
              </h4>
              <p className="text-sm text-muted-foreground leading-relaxed">
                To provide you with the best service, our team uses dedicated phone lines. You may receive account updates from our CRM support number and promotional offers from our designated marketing line. You can opt out of any of these individual channels at any time by replying <strong className="text-foreground font-medium">STOP</strong>.
              </p>
            </div>
          </div>

          <div ref={formRef} className={`lg:col-span-7 transition-all duration-1000 delay-200 ease-out ${isFormVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="bg-card p-8 md:p-10 rounded-2xl shadow-lg shadow-shadow-color/5 border border-border">
              <FormProvider formId="lead_form_home" sectionName="contact-inquiry" defaultValues={{
              firstName: "",
              lastName: "",
              email: "",
              phone: "",
              message: "",
              agreedToTexts: false
            }} submitText="Submit Inquiry" successMessage="Thank you for reaching out. Our team has received your inquiry and will be in touch shortly.">
                <InquiryForm />
              </FormProvider>
            </div>
          </div>

        </div>
      </div>
    </section>;
}